import { makeT } from "app/client/lib/localization";
import { AppModel } from "app/client/models/AppModel";
import { primaryButton } from "app/client/ui2018/buttons";
import { isNarrowScreenObs } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { isFreePlan } from "app/common/Features";
import { canUpgradeOrg } from "app/common/roles";

import { Computed, Disposable, dom, DomElementArg, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-productupgrade-");
const t = makeT("UpgradeButton");

/**
 * This is upgrade button shown in the top bar for free personal and team sites.
 *
 * When clicked, the plans modal is shown.
 */
export class UpgradeButton extends Disposable {
  private _canUpgradeSite: Observable<boolean>;
  private _domArgs: DomElementArg[];

  constructor(private _appModel: AppModel, ...args: DomElementArg[]) {
    super();

    this._domArgs = args;

    // Create observable for whether the user can upgrade.
    const isLoggedIn = !!this._appModel.currentValidUser;
    const planName = this._appModel.currentProduct?.name;
    const isOnFreeSite = Boolean(planName && isFreePlan(this._appModel.currentProduct?.name || ""));
    if (!isLoggedIn || !isOnFreeSite) {
      // Can't show nudge, we are not logged in or site can't be upgraded at all.
      this._canUpgradeSite = Observable.create(this, false);
    } else if (isOnFreeSite && !this._appModel.currentOrg?.owner) {
      // On free team site, we always can upgrade to a pro plan.
      this._canUpgradeSite = Observable.create(this, canUpgradeOrg(this._appModel.currentOrg));
    } else {
      // We are on personal site, so we nudge if user don't have any team site available
      this._canUpgradeSite = Computed.create(null, (use) => {
        return use(this._appModel.topAppModel.orgs).every(o => o.owner);
      });
    }
  }

  public buildDom() {
    return dom.maybe(this._canUpgradeSite, () => cssUpgradeButton(
      icon("Star",
        dom.hide(isNarrowScreenObs()),
      ),
      t("Upgrade"),
      dom.on("click", () => this._appModel.showUpgradeModal()),
      testId("upgrade-button"),
      ...this._domArgs,
    ));
  }
}

const cssUpgradeButton = styled(primaryButton, `
  display: flex;
  gap: 4px;
`);
